import { NodeData } from "@/api_model/Api.ts";
import { Label } from "@/components/Form";
import { EditFormWrapper } from "@/features/Edit/EditFormWrapper.tsx";
import { MultiFieldOptions } from "@/features/Edit/types.ts";
import { getAuthorized } from "@/lib/fetch.tsx";
import { a7FIAIssuersEditForm } from "@/pages/a7/EditForms/a7FIAIssuersEditForm.tsx";
import { a7FIAIssuerTsEditForm } from "@/pages/a7/EditForms/a7FIAIssuerTsEditForm.tsx";
import { Card, CardHeader, Divider } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export interface A7FIAIssuerMultiEditCardProps {
  issuer_id: number;
  allowDelete: boolean;
  selectedDate?: string;
  cardHeaderText?: string;
  issuerMultiFieldOptions?: MultiFieldOptions;
  issuerTsMultiFieldOptions?: MultiFieldOptions;
  set_societe_mere_id?: (societe_mere_id: number) => void;
}

export function A7FIAIssuerMultiEditCard({
  issuer_id,
  allowDelete,
  selectedDate,
  issuerMultiFieldOptions,
  issuerTsMultiFieldOptions,
  cardHeaderText,
  set_societe_mere_id,
}: A7FIAIssuerMultiEditCardProps) {
  const fieldChangeCallBack = useMemo(() => {
    return set_societe_mere_id
      ? {
          societe_mere_id: set_societe_mere_id,
        }
      : undefined;
  }, [set_societe_mere_id]);

  const { data: issuer_ts_id } = useQuery({
    queryKey: ["a7", "fia_multi_edit", "issuer_ts_id", issuer_id, selectedDate],
    queryFn: async () => {
      return getAuthorized(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/a7/fia_multi_edit/issuer_ts_id/${issuer_id}/${selectedDate}`,
      ).then((response) => response.json() as Promise<number | undefined>);
    },
    enabled: issuer_id != undefined && selectedDate != undefined,
    refetchOnWindowFocus: false,
  });

  const { data: issuer_funds } = useQuery({
    queryKey: [
      "a7",
      "fia_multi_edit",
      "issuer_fund_list",
      issuer_id,
      selectedDate,
    ],
    queryFn: async () => {
      return getAuthorized(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/a7/fia_multi_edit/issuer_fund_list/${issuer_id}/${selectedDate}`,
      ).then((response) => response.json() as Promise<NodeData[] | undefined>);
    },
    enabled: issuer_id != undefined && selectedDate != undefined,
    refetchOnWindowFocus: false,
  });

  return (
    <Card>
      <CardHeader className="text-lg font-bold text-gray-700">
        {cardHeaderText && <Label name={cardHeaderText} className="w-1/2" />}
        {issuer_funds && (
          <div className="w-1/2 flex flex-row-reverse">
            {issuer_funds.length === 1 ? (
              <Label className="text-xs" name={"Contenu dans 1 seul fonds"} />
            ) : (
              <Label
                className="text-xs text-red-600 "
                name={`Contenu dans ${issuer_funds.length} fonds`}
              />
            )}
          </div>
        )}
      </CardHeader>
      <Divider />
      <div className="flex flex-col p-2">
        <EditFormWrapper
          client={"a7"}
          id={issuer_id}
          allowDelete={allowDelete}
          multiFieldOptions={issuerMultiFieldOptions}
          fieldChangeCallBack={fieldChangeCallBack}
          {...a7FIAIssuersEditForm}
        />
        {issuer_ts_id ? (
          <EditFormWrapper
            client={"a7"}
            id={issuer_ts_id}
            allowDelete={allowDelete}
            multiFieldOptions={issuerTsMultiFieldOptions}
            {...a7FIAIssuerTsEditForm}
          />
        ) : (
          <div>Pas de série temporelle pour cet émetteur / date.</div>
        )}
      </div>
    </Card>
  );
}
